/**
 * InstitutionProfilePanel — canvas view for the Institution Context workspace.
 *
 * Displays collected profile fields, regulatory choices, key figures,
 * and open items. Updated in real-time via WORKSPACE_UPDATE WebSocket events.
 */

import React from 'react';
import { useProjectStore } from '../../stores/projectStore';
import { Building2, Scale, TrendingUp, AlertCircle, CheckCircle2 } from 'lucide-react';

export default function InstitutionProfilePanel() {
  const { institutionProfile } = useProjectStore();

  const hasAnyData =
    institutionProfile.profile_fields.length > 0 ||
    institutionProfile.regulatory_choices.length > 0 ||
    institutionProfile.key_figures.length > 0 ||
    institutionProfile.open_items.length > 0;

  return (
    <div className="h-full overflow-y-auto p-6 bg-bg-warm">
      {/* Header */}
      <div className="flex items-center gap-2 mb-6">
        <Building2 className="w-5 h-5 text-secondary" />
        <h2 className="text-lg font-semibold text-fg">Institution profile</h2>
        {institutionProfile.is_ready && (
          <span className="ml-auto text-xs font-medium text-success bg-success-bg px-2 py-0.5 rounded-full">
            Ready
          </span>
        )}
      </div>

      {!hasAnyData ? (
        <div className="text-sm text-fg-tertiary italic">
          Institution context will appear here as the agent collects information.
        </div>
      ) : (
        <div className="space-y-6">
          {/* Profile Fields */}
          {institutionProfile.profile_fields.length > 0 && (
            <div>
              <div className="text-xs font-medium text-fg-tertiary uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5" />
                Profile
              </div>
              <div className="grid gap-2">
                {institutionProfile.profile_fields.map((f) => (
                  <div key={f.field_id} className="flex items-baseline justify-between p-2.5 bg-bg-warm rounded border border-border-default">
                    <span className="text-sm text-fg-tertiary">{f.label}</span>
                    <span className="text-sm font-medium text-fg-secondary">
                      {f.value}
                      {f.confidence === 'inferred' && (
                        <span className="text-xs text-warning ml-1">(inferred)</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Regulatory Choices */}
          {institutionProfile.regulatory_choices.length > 0 && (
            <div>
              <div className="text-xs font-medium text-fg-tertiary uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <Scale className="w-3.5 h-3.5" />
                Regulatory choices
              </div>
              <div className="space-y-2">
                {institutionProfile.regulatory_choices.map((c) => (
                  <div key={c.choice_id} className="p-3 bg-bg-warm rounded border border-border-default">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-fg-secondary">{c.area}</span>
                      {c.confidence === 'inferred' && (
                        <span className="text-xs text-warning">(inferred)</span>
                      )}
                    </div>
                    <div className="text-sm text-fg-secondary">{c.approach}</div>
                    {c.scope_note && (
                      <div className="text-xs text-fg-tertiary mt-1">{c.scope_note}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Key Figures */}
          {institutionProfile.key_figures.length > 0 && (
            <div>
              <div className="text-xs font-medium text-fg-tertiary uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5" />
                Key figures
              </div>
              <div className="grid grid-cols-2 gap-2">
                {institutionProfile.key_figures.map((f) => (
                  <div key={f.figure_id} className="p-2.5 bg-bg-warm rounded border border-border-default">
                    <div className="text-xs text-fg-tertiary">{f.metric}</div>
                    <div className="text-sm font-semibold text-fg-secondary">{f.value}</div>
                    {f.as_of_date && <div className="text-xs text-fg-tertiary">as of {f.as_of_date}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Open Items */}
          {institutionProfile.open_items.length > 0 && (
            <div>
              <div className="text-xs font-medium text-fg-tertiary uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5" />
                Open items
              </div>
              <ul className="space-y-2">
                {institutionProfile.open_items.map((item) => (
                  <li key={item.item_id} className="flex items-start gap-2 p-2 bg-bg-warm rounded border border-border-default">
                    {item.status === 'resolved' ? (
                      <CheckCircle2 className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-warning mt-0.5 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm ${item.status === 'resolved' ? 'text-fg-tertiary line-through' : 'text-fg-secondary'}`}>
                        {item.description}
                      </div>
                      {item.resolution && (
                        <div className="text-xs text-success mt-0.5">{item.resolution}</div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
